/**
 * @param {string|Date} dateString The input date/time string or Date object
 * @returns {string} Formatted string "DD-MM-YYYY HH:mm" or "DD-MM-YYYY"
 */
export const formatDateForAPI = (dateString) => {
    // Check if dateString includes time (format: "YYYY-MM-DD HH:mm")
    if (typeof dateString === 'string' && dateString.includes(' ')) {
        const [datePart, timePart] = dateString.split(' ');
        const d = new Date(datePart);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        // Return in format: DD-MM-YYYY HH:mm
        return `${day}-${month}-${year} ${timePart}`;
    }
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

/**
 * @param {string|Date} date The input date
 * @returns {string} Formatted display string
 */
export const formatDateForDisplay = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * First day of the current month through yesterday. On the 1st of a month this
 * resolves to the whole previous month, since the current month has no past days yet.
 * @param {Date} [base=new Date()]
 * @returns {{ start: Date, end: Date }}
 */
export const getMonthToDateRange = (base = new Date()) => {
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1);
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { start, end };
};

/**
 * @param {object} params Filter parameters { date, startDate, endDate }
 * @returns {string} Period text
 */
export const getPeriodText = (params) => {
    if (!params) return '';

    // If custom date range
    if (params.date === 'custom' && params.startDate && params.endDate) {
        const splitDateTime = (value) => {
            if (typeof value === 'string' && value.includes(' ')) {
                const [datePart, timePart] = value.split(' ');
                return { datePart, timePart };
            }
            return { datePart: value, timePart: null };
        };

        const formatDateShort = (date) =>
            new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });

        const formatTime12 = (timePart) => {
            if (!timePart) return '';
            const [hourStr, minuteStr] = timePart.split(':');
            const hourNum = Number(hourStr);
            if (Number.isNaN(hourNum)) return timePart;
            const period = hourNum >= 12 ? 'PM' : 'AM';
            const hour12 = hourNum % 12 === 0 ? 12 : hourNum % 12;
            return `${hour12}:${minuteStr} ${period}`;
        };

        const { datePart: startDateOnly, timePart: startTime } = splitDateTime(params.startDate);
        const { datePart: endDateOnly, timePart: endTime } = splitDateTime(params.endDate);

        const startText = `${formatDateShort(startDateOnly)}${startTime ? ` ${formatTime12(startTime)}` : ''}`;
        const endText = `${formatDateShort(endDateOnly)}${endTime ? ` ${formatTime12(endTime)}` : ''}`;

        return `${startText} - ${endText}`;
    }

    if (params.date === 'monthToDate') {
        const { start, end } = getMonthToDateRange();
        const formatDateShort = (date) =>
            date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${formatDateShort(start)} - ${formatDateShort(end)}`;
    }

    // For predefined periods
    const dateLabels = {
        'today': 'Today',
        'yesterday': 'Yesterday',
        'thisWeek': 'This Week',
        'lastWeek': 'Last Week',
        'lastTwoWeeks': 'Last Two Weeks'
    };

    // Check if it's a predefined period
    if (dateLabels[params.date]) {
        return dateLabels[params.date];
    }

    // Handle monthly selections (format: "month-YYYY-M")
    if (params.date && params.date.startsWith('month-')) {
        const [, year, month] = params.date.split('-');
        const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        return monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    // Fallback to current date
    return formatDateForDisplay(new Date());
};

/**
 * @param {object} filterParams Filter parameters { date, startDate, endDate }
 * @returns {object} { startDate, endDate } formatted for API
 */
export const calculateReportDateRange = (filterParams) => {
    let startDate, endDate;
    const today = new Date();

    if (filterParams.date === 'custom' && filterParams.startDate && filterParams.endDate) {
        startDate = formatDateForAPI(filterParams.startDate);
        endDate = formatDateForAPI(filterParams.endDate);
    } else {
        // Calculate dates for predefined periods
        switch (filterParams.date) {
            case 'today':
                startDate = endDate = formatDateForAPI(today);
                break;
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                startDate = endDate = formatDateForAPI(yesterday);
                break;
            case 'thisWeek':
                const startOfWeek = new Date(today);
                const dayOfWeek = today.getDay();
                const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                startOfWeek.setDate(today.getDate() - daysFromMonday);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                startDate = formatDateForAPI(startOfWeek);
                endDate = formatDateForAPI(endOfWeek);
                break;
            case 'lastWeek':
                const lastWeekStart = new Date(today);
                const dow = today.getDay();
                const fromMon = dow === 0 ? 6 : dow - 1;
                lastWeekStart.setDate(today.getDate() - fromMon - 7);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                startDate = formatDateForAPI(lastWeekStart);
                endDate = formatDateForAPI(lastWeekEnd);
                break;
            case 'lastTwoWeeks':
                const lastSunday = new Date(today);
                lastSunday.setDate(today.getDate() - today.getDay());
                const twoWeekStart = new Date(lastSunday);
                twoWeekStart.setDate(lastSunday.getDate() - 13);
                startDate = formatDateForAPI(twoWeekStart);
                endDate = formatDateForAPI(lastSunday);
                break;
            case 'monthToDate': {
                const { start, end } = getMonthToDateRange(today);
                startDate = formatDateForAPI(start);
                endDate = formatDateForAPI(end);
                break;
            }
            default:
                // For monthly options
                if (filterParams.date?.startsWith('month-')) {
                    const [, year, month] = filterParams.date.split('-');
                    const monthStart = new Date(parseInt(year), parseInt(month) - 1, 1);
                    const monthEnd = new Date(parseInt(year), parseInt(month), 0);
                    startDate = formatDateForAPI(monthStart);
                    endDate = formatDateForAPI(monthEnd);
                } else {
                    startDate = endDate = formatDateForAPI(today);
                }
        }
    }

    // Add default time range 00:00 - 23:59
    // Only use custom time if it's a custom date AND time was explicitly selected
    let startDateTime = startDate;
    let endDateTime = endDate;
    
    if (filterParams.date === 'custom' && filterParams.startDate && filterParams.endDate) {
        // Check if filterParams already includes time
        const startHasTime = typeof filterParams.startDate === 'string' && filterParams.startDate.includes(' ');
        const endHasTime = typeof filterParams.endDate === 'string' && filterParams.endDate.includes(' ');
        
        if (startHasTime && endHasTime) {
            // Use the time provided in filterParams
            startDateTime = startDate; // Already formatted with time
            endDateTime = endDate; // Already formatted with time
        } else {
            // Add default time range
            startDateTime = `${startDate} 00:00`;
            endDateTime = `${endDate} 23:59`;
        }
    } else {
        // For all predefined periods (today, yesterday, thisWeek, etc.)
        // Always add default time range
        startDateTime = `${startDate} 00:00`;
        endDateTime = `${endDate} 23:59`;
    }

    return {
        startDate: startDateTime,
        endDate: endDateTime
    };
};

/**
 * @param {object} filterParams Filter parameters { date, startDate, endDate }
 * @returns {object} { startDate, endDate } as Date objects
 */
export const getReportDateRangeDates = (filterParams) => {
    if (!filterParams) {
        return { startDate: null, endDate: null };
    }

    let startDate;
    let endDate;
    const today = new Date();

    const parseDateOnly = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
            const datePart = value.includes(' ') ? value.split(' ')[0] : value;
            return new Date(datePart);
        }
        return new Date(value);
    };

    if (filterParams.date === 'custom' && filterParams.startDate && filterParams.endDate) {
        startDate = parseDateOnly(filterParams.startDate);
        endDate = parseDateOnly(filterParams.endDate);
    } else {
        switch (filterParams.date) {
            case 'today':
                startDate = new Date(today);
                endDate = new Date(today);
                break;
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                startDate = new Date(yesterday);
                endDate = new Date(yesterday);
                break;
            case 'thisWeek':
                const startOfWeek = new Date(today);
                const dayOfWeek = today.getDay();
                const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                startOfWeek.setDate(today.getDate() - daysFromMonday);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                startDate = startOfWeek;
                endDate = endOfWeek;
                break;
            case 'lastWeek':
                const lastWeekStart = new Date(today);
                const dow = today.getDay();
                const fromMon = dow === 0 ? 6 : dow - 1;
                lastWeekStart.setDate(today.getDate() - fromMon - 7);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                startDate = lastWeekStart;
                endDate = lastWeekEnd;
                break;
            case 'lastTwoWeeks':
                const lastSunday = new Date(today);
                lastSunday.setDate(today.getDate() - today.getDay());
                const twoWeekStart = new Date(lastSunday);
                twoWeekStart.setDate(lastSunday.getDate() - 13);
                startDate = twoWeekStart;
                endDate = lastSunday;
                break;
            case 'monthToDate': {
                const { start, end } = getMonthToDateRange(today);
                startDate = start;
                endDate = end;
                break;
            }
            default:
                if (filterParams.date?.startsWith('month-')) {
                    const [, year, month] = filterParams.date.split('-');
                    const monthStart = new Date(parseInt(year), parseInt(month) - 1, 1);
                    const monthEnd = new Date(parseInt(year), parseInt(month), 0);
                    startDate = monthStart;
                    endDate = monthEnd;
                } else {
                    startDate = new Date(today);
                    endDate = new Date(today);
                }
        }
    }

    return { startDate, endDate };
};

/**
 * @param {object} filterParams Filter parameters { date, startDate, endDate }
 * @param {number} months Max duration in months
 * @returns {boolean} true if endDate <= startDate + months
 */
export const isReportRangeWithinMonths = (filterParams, months = 3) => {
    const { startDate, endDate } = getReportDateRangeDates(filterParams);
    if (!startDate || !endDate) return true;
    const limit = new Date(startDate);
    limit.setMonth(limit.getMonth() + months);
    return endDate <= limit;
};
